// The server's JSON API (src/voitta_kvm/app/main.py). Every call is same-origin with the session cookie.

export type CodexState = { connected: boolean; available?: boolean; since?: number | null; error?: string;
  pending?: { url: string; code: string; expires: number } };
export type Me = {
  email: string;
  role: "admin" | "user";
  superadmin?: boolean; // server capability, independent of the editable admin role
  viewed_by?: string | null; // the superadmin "viewing as" this user right now, else null
  claude: { connected: boolean; since: number | null; hint: string | null };
  codex: CodexState;
};
export type TargetT = {
  id: string; name: string; kind: "kvm" | "rdp" | "vnc" | "sim"; os: string;
  settings: Record<string, unknown>; secrets_set: string[]; holder?: string | null;
  machine_id?: string;     // the computer's id once it has more than one connection; empty = its only one
  via?: string | null;     // how the holder holds it: web (a tab) or mcp (their own Claude Code)
  computer_id?: string; computer?: string; kvm_id?: string;
  path?: string;           // the way in: "KVM1", "VNC", "RDP", "simulator"
  kvm_busy?: string | null;   // its KVM shows another computer now ("KVM1 is in use")
  cable?: string | null;      // its KVM has another computer on its cable
};
// One way into a computer, as /api/computers lists it ("via" is the way in here; "via_holder" web or mcp).
export type PathT = Omit<TargetT, "via"> & { via: string; via_holder: string | null;
  error?: { at: number; message: string } | null;     // its last failed open, until a good one or new settings
  learned?: LearnedT[] };                             // what the harness learned on it, per screen (Computers only)
// What the harness learned on a way in for one screen size: when, and the wheel's way.
export type LearnedT = { os: string; screen: string; calibration?: number; tuning?: number; wheel?: string };
// A computer's own value: a secret's value is never listed, only fetched one at a time (api.computerValue).
export type ValueT = { name: string; secret: boolean; value: string | null; by: string; updated_at: number };
// A KVM box's own login, as a computer through it shows it: user and password only to the box's managers.
export type KvmLoginT = { kvm_id: string; name: string; access: "manage" | null; user: string | null;
  password_set: boolean | null; also: string[]; others: number };
export type Access = "manage" | "use" | "attach";
export type ShareT = { email: string; level: Access; by: string; at: number };
export type ComputerT = { id: string; name: string; os: string; owner: string; description?: string; secrets_set: string[];
  access: Access; paths: PathT[]; shares?: ShareT[]; values?: ValueT[]; kvm_logins?: KvmLoginT[] };
export type KvmT = { id: string; name: string; settings: Record<string, unknown>; secrets_set: string[]; on_cable: string;
  owner: string; access: Access | null; computers: { id: string; name: string; path: string }[]; others: number;
  shares?: ShareT[]; description?: string };
export type SecretT = { name: string; description: string; updated_at: number };
export type ApiKey = { id: string; owner: string; name: string; prefix: string; created_at: number; last_used: number | null };
export type UserT = { email: string; role: "admin" | "user"; disabled: boolean; added_by: string | null;
  added_at: number; last_seen: number | null; keys?: number };
export type Conversation = { id: string; title: string; brain: string; session_id: string | null; updated_at: number;
  ctx?: string | null };   // the context meter's latest numbers (JSON), see ContextMeter.tsx
export type StepRow = { n: number; tool: string; input: Record<string, unknown>; verdict: string; text: string; ms: number | null; shot: string | null };
export type RunRow = { id: string; message: string; status: string; summary: string; started_at: number;
  ended_at: number | null; steps: StepRow[]; verdict: Verdict | null; verdict_note: string | null; ready_s?: number | null };
export type ActivityLine = { class: "progress" | "milestone" | "warning"; kind: string; label: string;
  level: "info" | "warn" | "error"; at: number | null; count: number };
export type Verdict = "up" | "down";
export type RunFilters = { target?: string; user?: string; status?: string; verdict?: string; days?: number };
export type AdminRun = { id: string; message: string; status: string; summary: string; started_at: number; ended_at: number | null;
  model: string | null; steps: number | null; seconds: number | null; ready_s: number | null; cost_usd: number | null;
  verdict: Verdict | null; verdict_note: string | null; owner: string; target_id: string; target: string };
export type ToolStat = { tool: string; calls: number; failed: number; fail_rate: number; median_ms: number | null };
export type Stats = { runs: number; agent_pass_rate: number | null; people_pass_rate: number | null; judged: number;
  agent_passed_people_down: number; cost_usd: number; median_seconds: number | null; median_step_s: number | null;
  ready_median_s: number | null; ready_p90_s: number | null; statuses: Record<string, number>;
  days: { day: string; runs: number; passed: number; cost_usd: number }[]; tools: ToolStat[];
  warnings: { kind: string; count: number; runs: number }[] };
export type RunDetail = { run: AdminRun & { owner: string }; record: Record<string, unknown>; steps: StepRow[];
  warnings: Record<string, unknown>[]; takeovers: Record<string, unknown>[]; files: boolean; shots: string[] };
export type Storage = { usage: Record<string, number>; policy: Record<string, number>;
  last_clean: { t: number; removed: number; freed: number } | null; last_export: number | null; training: string | null };
export type Service = { name: string; ok: boolean; configured: boolean; ms: number | null; error: string };

export type AccessEvidence = { at: number; actor: string; source: "admission" | "run" | "heartbeat" | "sign-in" | "api-key" };
export type FleetHold = { target_id: string; holder: string; since: number; heartbeat: number; via: string; expires_at: number };
export type FleetSession = "disconnected" | "connecting" | "closing" | "open";
export type FleetResource = { id: string; name: string; owner: string; created_at: number; path_ids: string[];
  shares: ShareT[]; holds: FleetHold[]; last_access: AccessEvidence | null; session: FleetSession; power: "unknown" };
export type FleetPath = { id: string; kind: TargetT["kind"]; computer_id: string; kvm_id: string; session: FleetSession;
  activity: string | null; power: "unknown"; last_access: AccessEvidence | null; hold: FleetHold | null;
  last_heartbeat: number | null; cable: "current" | "other" | "unknown" | null };
export type FleetOverview = { generated_at: number; lease_expiry_s: number;
  counts: { accounts: number; computers: number; kvms: number; paths: number; active_holds: number };
  accounts: (UserT & { superadmin: boolean; last_access: AccessEvidence | null })[];
  computers: (FleetResource & { os: string })[]; kvms: (FleetResource & { on_cable: string })[]; paths: FleetPath[] };

export class HttpError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

const qs = (f: Record<string, unknown>) =>
  new URLSearchParams(Object.entries(f).filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => [k, String(v)])).toString();

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!r.ok) {
    let msg = r.statusText;
    try { msg = (await r.json()).detail ?? msg; } catch { /* not JSON */ }
    throw new HttpError(r.status, typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return r.json() as Promise<T>;
}

export const api = {
  me: () => call<Me>("GET", "/api/me"),
  overview: () => call<FleetOverview>("GET", "/api/superadmin/overview"),
  viewAs: (email: string) => call<{ ok: boolean; email: string }>("POST", "/api/superadmin/view-as", { email }),
  stopViewAs: () => call<{ ok: boolean }>("DELETE", "/api/superadmin/view-as"),
  logout: () => call<{ ok: boolean }>("POST", "/auth/logout"),
  targets: () => call<TargetT[]>("GET", "/api/targets"),
  services: () => call<Service[]>("GET", "/api/services"),
  conversations: (tid: string) => call<Conversation[]>("GET", `/api/targets/${tid}/conversations`),
  conversation: (cid: string) => call<Conversation & { runs: RunRow[] }>("GET", `/api/conversations/${cid}`),
  adminRuns: (f: RunFilters) => call<AdminRun[]>("GET", `/api/admin/runs?${qs(f)}`),
  stats: (f: RunFilters) => call<Stats>("GET", `/api/admin/stats?${qs(f)}`),
  adminRun: (rid: string) => call<RunDetail>("GET", `/api/admin/runs/${rid}`),
  storage: () => call<Storage>("GET", "/api/admin/storage"),
  clean: (dryRun: boolean) => call<{ removed: number; freed: number; items: string[] }>("POST", `/api/admin/storage/clean?dry_run=${dryRun}`),
  runActivity: (rid: string, q: { item?: number; stage?: string }) =>
    call<{ lines: ActivityLine[]; gone: boolean }>("GET", `/api/runs/${rid}/activity?${q.item != null ? `item=${q.item}` : `stage=${q.stage}`}`),
  verdict: (rid: string, verdict: Verdict | null, note = "") =>
    call<{ verdict: Verdict | null; note: string }>("POST", `/api/runs/${rid}/verdict`, { verdict, note }),
  connectClaude: (token: string) => call<Me["claude"]>("POST", "/api/account/claude", { token }),
  disconnectClaude: () => call<Me["claude"]>("DELETE", "/api/account/claude"),
  connectCodex: () => call<{ url: string; code: string; expires: number }>("POST", "/api/account/codex"),
  keys: () => call<ApiKey[]>("GET", "/api/keys"),
  adminKeys: () => call<ApiKey[]>("GET", "/api/admin/keys"),
  newKey: (name: string) => call<{ key: ApiKey; secret: string; setup: string }>("POST", "/api/keys", { name }),
  secrets: () => call<SecretT[]>("GET", "/api/secrets"),
  saveSecret: (s: { name: string; description: string; value: string }) => call<SecretT[]>("POST", "/api/secrets", s),
  removeSecret: (name: string) => call<SecretT[]>("DELETE", `/api/secrets/${encodeURIComponent(name)}`),
  phones: () => call<{ id: string; device: string | null; created_at: number; last_used: number | null }[]>("GET", "/api/phones"),
  newPairing: () => call<{ id: string; expires_at: number; link: string; qr: string }>("POST", "/api/pairing"),
  pairing: (id: string) => call<{ state: "waiting" | "paired" | "expired"; device: string | null; expires_at: number }>(
    "GET", `/api/pairing/${encodeURIComponent(id)}`),
  revokeKey: (id: string) => call<{ ok: boolean }>("DELETE", `/api/keys/${encodeURIComponent(id)}`),
  codexState: () => call<CodexState>("GET", "/api/account/codex"),
  disconnectCodex: () => call<CodexState>("DELETE", "/api/account/codex"),
  users: () => call<UserT[]>("GET", "/api/admin/users"),
  addUser: (email: string, role: string) => call<UserT>("POST", "/api/admin/users", { email, role }),
  updateUser: (email: string, patch: { role?: string; disabled?: boolean }) =>
    call<UserT>("PATCH", `/api/admin/users/${encodeURIComponent(email)}`, { email, ...patch }),
  removeUser: (email: string) => call("DELETE", `/api/admin/users/${encodeURIComponent(email)}`),
  adminTargets: () => call<TargetT[]>("GET", "/api/admin/targets"),
  saveTarget: (t: Partial<TargetT> & { secrets?: Record<string, string> }) => call<TargetT>("POST", "/api/admin/targets", t),
  removeTarget: (id: string) => call("DELETE", `/api/admin/targets/${id}`),
  breakLease: (id: string) => call("DELETE", `/api/admin/targets/${id}/lease`),
  computers: () => call<ComputerT[]>("GET", "/api/computers"),
  saveComputer: (c: { id?: string; name: string; os: string; secrets: Record<string, string> }) =>
    call<ComputerT>("POST", "/api/computers", c),
  removeComputer: (id: string) => call("DELETE", `/api/computers/${id}`),
  // one stored value in full (its PC sign-in, a way in's login, or its own): written to the audit as shown or copied
  computerValue: (cid: string, name: string, why: "show" | "copy") =>
    call<{ value: string }>("GET", `/api/computers/${cid}/values/${encodeURIComponent(name)}?why=${why}`),
  saveValue: (cid: string, name: string, v: { value: string; secret: boolean; old?: string }) =>
    call<ValueT>("PUT", `/api/computers/${cid}/values/${encodeURIComponent(name)}`, v),
  removeValue: (cid: string, name: string) => call("DELETE", `/api/computers/${cid}/values/${encodeURIComponent(name)}`),
  kvmPassword: (kid: string, why: "show" | "copy") =>
    call<{ value: string }>("GET", `/api/kvms/${kid}/password?why=${why}`),
  describe: (kind: "computer" | "kvm", id: string, text: string) =>
    call<{ description: string }>("PUT", `/api/${kind === "computer" ? "computers" : "kvms"}/${id}/description`, { text }),
  resetLearned: (pid: string) => call<{ ok: boolean; forgot: string[] }>("POST", `/api/paths/${pid}/learned/reset`),
  savePath: (cid: string, p: { id?: string; kind: string; kvm_id?: string; settings: Record<string, unknown> }) =>
    call<PathT>("POST", `/api/computers/${cid}/paths`, p),
  removePath: (id: string) => call("DELETE", `/api/paths/${id}`),
  releasePath: (id: string) => call("POST", `/api/paths/${id}/release`),
  splitPath: (id: string, name: string) => call<ComputerT>("POST", `/api/paths/${id}/split`, { name }),
  merge: (keep: string, other: string) => call<ComputerT>("POST", "/api/computers/merge", { keep, other }),
  kvms: () => call<KvmT[]>("GET", "/api/kvms"),
  saveKvm: (k: { id?: string; name: string; settings: Record<string, unknown>; secrets: Record<string, string> }) =>
    call<KvmT>("POST", "/api/kvms", k),
  removeKvm: (id: string) => call("DELETE", `/api/kvms/${id}`),
  setCable: (kid: string, computer_id: string) => call<KvmT>("POST", `/api/kvms/${kid}/cable`, { computer_id }),
  people: () => call<string[]>("GET", "/api/people"),
  share: (kind: "computer" | "kvm", id: string, email: string, level: Access) =>
    call<ShareT[]>("POST", `/api/${kind}s/${id}/shares`, { email, level }),
  unshare: (kind: "computer" | "kvm", id: string, email: string) =>
    call<ShareT[]>("DELETE", `/api/${kind}s/${id}/shares/${encodeURIComponent(email)}`),
};
